import type { Timeout } from "@utils/types"
import { computed, onUnmounted } from "vue"
import { ref, watch, type Ref } from "vue"
import type { BufferingProps } from "./types"

export const SCHEDULE_FLUSH_MS = 500

export function useFlushTimer (callback: () => void, timeout: number = SCHEDULE_FLUSH_MS) {
  const flushTimer = ref<Timeout>()

  // Clear flush timer if flushed manually
  const abortFlushTimer = () => {
    if (flushTimer.value) {
      clearTimeout(flushTimer.value)

      flushTimer.value = undefined
    }
  }

  // When triggering flush timer again, clear flush timer and start it again
  const startFlushTimer = () => {
    abortFlushTimer()

    flushTimer.value = setTimeout(() => {
      abortFlushTimer()

      callback()
    }, timeout)
  }

  // Ensure timer is cleared
  onUnmounted(abortFlushTimer)

  return {
    abortFlushTimer,
    startFlushTimer
  }
}

export function useBuffering <T>(modelValue: Ref<T>, props: BufferingProps) {
  if (props.buffered) {
    const options = Object.assign({ delay: SCHEDULE_FLUSH_MS }, typeof props.buffered === 'boolean' ? {} : props.buffered)

    const intermediateModel = ref(modelValue.value)

    const updateModelValue = () => {
      modelValue.value = intermediateModel.value
    }

    const { startFlushTimer, abortFlushTimer } = useFlushTimer(updateModelValue, options.delay)

    // When model value is changed, immediately set the intermediate value and clear flush timer
    watch(modelValue, (value) => {
      abortFlushTimer()

      intermediateModel.value = value
    })

    return {
      intermediateModel,
      scheduleFlush: startFlushTimer,
      flush: () => {
        abortFlushTimer()

        updateModelValue()
      }
    }
  } else {
    return {
      /** Delay update one extra tick */
      intermediateModel: computed({
        get: () => modelValue.value,
        set: (value) => modelValue.value = value
      }),
      scheduleFlush: () => {},
      flush: () => {}
    }
  }
}